import { z } from 'zod';

const signalState = z.enum(['healthy', 'degraded', 'unavailable', 'unconfigured', 'stale', 'unknown']);

const signalSchema = z.object({
  key: z.string(),
  label: z.string(),
  state: signalState,
  basis: z.enum(['measured', 'inferred']),
  detail: z.string(),
  checkedAt: z.string().nullable(),
});

const inventorySchema = z.object({
  name: z.string(),
  description: z.string(),
  required: z.boolean(),
  sensitive: z.boolean(),
  configured: z.boolean(),
  reload: z.string(),
});

const integrationSchema = z.object({
  id: z.string(),
  label: z.string(),
  state: signalState,
  basis: z.enum(['measured', 'inferred']),
  detail: z.string(),
  testable: z.boolean(),
  checkedAt: z.string().nullable(),
});

const tenantSchema = z.object({
  slug: z.string(),
  name: z.string(),
  status: z.string(),
  database: z.string(),
  detail: z.string(),
});

const archiveSchema = z.object({
  id: z.string(),
  createdAt: z.string().nullable(),
  slugs: z.array(z.string()),
  bytes: z.number().nullable(),
  checksumRecorded: z.boolean(),
});

const failedJobSchema = z.object({
  id: z.string(),
  name: z.string(),
  failedAt: z.string().nullable(),
});

const controlSchema = z.object({
  checkedAt: z.string(),
  signals: z.array(signalSchema),
  inventory: z.array(inventorySchema),
  integrations: z.array(integrationSchema),
  tenants: z.array(tenantSchema),
  archives: z.array(archiveSchema),
  failedJobs: z.object({
    state: z.string(),
    count: z.number(),
    jobs: z.array(failedJobSchema),
  }),
  securityEvents: z.array(z.object({
    id: z.string(),
    eventType: z.string(),
    actorName: z.string(),
    createdAt: z.string(),
    reason: z.string().nullable(),
  })),
  unavailable: z.array(z.object({
    name: z.string(),
    reason: z.string(),
  })),
});

export type PlatformControl = z.infer<typeof controlSchema>;
export type ControlTenant = z.infer<typeof tenantSchema>;

const probeSchema = z.object({
  status: z.string(),
  latencyMs: z.number().nullable(),
  error: z.string().nullable(),
});

export type TenantProbe = z.infer<typeof probeSchema>;

export function parsePlatformControl(payload: unknown): PlatformControl {
  const parsed = controlSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The control center could not be read.');
  return parsed.data;
}

export function parseTenantProbe(payload: unknown): TenantProbe {
  const parsed = probeSchema.safeParse(payload);
  if (!parsed.success) throw new Error('The database check could not be read.');
  return parsed.data;
}
